/**
 * PWA install prompt orchestration.
 *
 * Three platforms, three behaviours:
 *
 *   1. **Chromium** (Chrome / Edge / Android Chrome) — fires
 *      `beforeinstallprompt` after engagement criteria are met (HTTPS,
 *      manifest, registered SW, ~30s of interaction). We `preventDefault`
 *      to suppress the browser's native mini-bar, stash the event, and
 *      offer our own banner. Tapping Install calls `event.prompt()`;
 *      browser handles the rest. The event is single-shot per page load.
 *
 *   2. **iOS Safari** — no `beforeinstallprompt` event exists. Install
 *      requires the user to tap Share → Add to Home Screen manually.
 *      We detect the platform via UA and surface an instructional banner
 *      (no actionable button). Other iOS browsers (Chrome/Firefox on iOS)
 *      can't install PWAs at all — they share Safari's WebKit but the
 *      install path is Safari-only.
 *
 *   3. **Already installed** — bail out entirely. Detection covers both
 *      Chromium standalone (`display-mode: standalone`) and iOS Safari
 *      legacy (`navigator.standalone`).
 *
 * Dismissal cooldown — incremental, so a user who isn't interested
 * doesn't get pestered, but isn't silently locked out forever:
 *
 *   1st dismiss → wait 7  days
 *   2nd dismiss → wait 10 days
 *   3rd+ dismiss → wait 30 days (capped)
 *
 * The Settings escape-hatch ignores the cooldown — if the user goes
 * looking for it, they're opting in regardless.
 */

import { useCallback, useEffect, useState } from "react";

const DISMISS_COOLDOWN_DAYS = [7, 10, 30] as const;

const STORAGE_KEY_AT = "rmnlng.pwaInstallDismissedAt";
const STORAGE_KEY_COUNT = "rmnlng.pwaInstallDismissCount";

export type PWAInstallPlatform = "chromium" | "ios-safari" | "unsupported";

// Shape of `beforeinstallprompt` — not in the stock TS lib because the
// API still lives in WICG, even though every Chromium implementation
// follows the same shape.
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: ReadonlyArray<string>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
  prompt(): Promise<void>;
}

function detectPlatform(): PWAInstallPlatform {
  if (typeof window === "undefined") return "unsupported";
  const ua = window.navigator.userAgent;
  // iPadOS 13+ identifies as Mac unless "Request Mobile Site" is on —
  // the touch-points heuristic covers the desktop-class iPad case.
  const isIPad = /iPad/.test(ua) || (ua.includes("Mac") && navigator.maxTouchPoints > 1);
  const isIPhone = /iPhone|iPod/.test(ua);
  const isIOS = (isIPad || isIPhone) && !/MSStream/.test(ua);
  // PWA install on iOS only works from real Safari. Chrome (CriOS),
  // Firefox (FxiOS), Edge (EdgiOS) etc. share WebKit but block
  // Add-to-Home-Screen in their app menus.
  if (isIOS && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)) return "ios-safari";
  // Anything else *might* support `beforeinstallprompt`. Whether the
  // event actually fires is up to the browser — we just listen and
  // use it if we get it.
  return "chromium";
}

function detectInstalled(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  // iOS Safari uses a legacy boolean property on navigator instead of
  // the standard media query.
  return (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function readDismissalState(): { dismissedAt: number | null; count: number } {
  if (typeof window === "undefined") return { dismissedAt: null, count: 0 };
  const at = window.localStorage.getItem(STORAGE_KEY_AT);
  const count = window.localStorage.getItem(STORAGE_KEY_COUNT);
  return {
    dismissedAt: at ? Number(at) : null,
    count: count ? Number(count) : 0,
  };
}

function cooldownElapsed(dismissedAt: number | null, count: number): boolean {
  if (dismissedAt === null) return true;
  // count − 1 because the count was incremented at dismiss time;
  // we want "Nth dismissal" → DISMISS_COOLDOWN_DAYS[N-1].
  const idx = Math.min(count - 1, DISMISS_COOLDOWN_DAYS.length - 1);
  const cooldownMs = DISMISS_COOLDOWN_DAYS[idx]! * 24 * 60 * 60 * 1000;
  return Date.now() - dismissedAt >= cooldownMs;
}

export interface UsePWAInstall {
  platform: PWAInstallPlatform;
  installed: boolean;
  /** Whether the floating banner should render right now (cooldown-aware). */
  shouldShowBanner: boolean;
  /** Whether the Settings escape-hatch row should render (ignores cooldown). */
  shouldShowSettingsRow: boolean;
  /**
   * Trigger the native install prompt. No-op on iOS (no API exists —
   * the user has to do Share → Add to Home Screen manually).
   */
  install: () => Promise<void>;
  /** Bumps the dismissal counter and starts the cooldown for the banner. */
  dismiss: () => void;
}

export function usePWAInstall(): UsePWAInstall {
  const [platform] = useState<PWAInstallPlatform>(detectPlatform);
  const [installed, setInstalled] = useState<boolean>(detectInstalled);
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  // Tracked in React state so a Settings-button install or banner ✕
  // re-evaluates without a full reload.
  const [{ dismissedAt, count }, setDismissal] = useState(readDismissalState);

  // Capture `beforeinstallprompt` (Chromium only). One-shot per page
  // load — the browser fires it once after engagement criteria, and
  // never again until the next page load (or the user uninstalls).
  useEffect(() => {
    if (platform !== "chromium") return;
    const handler = (e: Event) => {
      e.preventDefault();
      setEvent(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [platform]);

  // `appinstalled` fires on a successful install (Chromium). Hide
  // everything once it does — the cooldown becomes moot and the
  // captured event is gone.
  useEffect(() => {
    const handler = () => {
      setInstalled(true);
      setEvent(null);
    };
    window.addEventListener("appinstalled", handler);
    return () => window.removeEventListener("appinstalled", handler);
  }, []);

  const install = useCallback(async () => {
    if (!event) return;
    await event.prompt();
    // Event is consumed regardless of outcome — Chromium won't fire
    // it again on this page. Clear so the Settings row hides itself.
    setEvent(null);
  }, [event]);

  const dismiss = useCallback(() => {
    const newCount = count + 1;
    const at = Date.now();
    window.localStorage.setItem(STORAGE_KEY_AT, String(at));
    window.localStorage.setItem(STORAGE_KEY_COUNT, String(newCount));
    setDismissal({ dismissedAt: at, count: newCount });
  }, [count]);

  // Chromium banner needs a captured event to even show the Install
  // button (no event = no API to call). iOS banner is purely
  // instructional and shows whenever we're not already installed.
  const canPromptChromium = platform === "chromium" && event !== null;
  const isIOS = platform === "ios-safari";
  const eligible = !installed && (canPromptChromium || isIOS);
  const shouldShowBanner = eligible && cooldownElapsed(dismissedAt, count);
  const shouldShowSettingsRow = !installed && (canPromptChromium || isIOS);

  return { platform, installed, shouldShowBanner, shouldShowSettingsRow, install, dismiss };
}
